import { assertNever } from '@kofno/piper';
import clsx from 'clsx';
import { observer } from 'mobx-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { T } from '../../../../Translations';
import { awaitingCourse } from './CourseField';
import { participantSearchStore } from './ParticipantSearch/Stores/ParticipantSearchStore';
import { shareStore } from './Stores/ShareStore';

type Placement = 'top' | 'bottom';

// The footer's Cancel sits flush with the content edge, as on Add Participant.
const cancelPadding = (placement: Placement): string => {
  switch (placement) {
    case 'top':
      return 'px-4 py-2';
    case 'bottom':
      return '';
    default:
      return assertNever(placement);
  }
};

interface Props {
  onConfirm: () => void;
  placement: Placement;
}

// Cancel and the primary action, styled like the Add Participant and Add
// Group pages. The header lays them out side by side, the footer spreads
// them to either edge; the parent owns the layout.
function ActionButtons({ onConfirm, placement }: Props) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const hasConfirmedParticipants = shareStore.participants.some(
    (participant) => participant.status === 'verified',
  );

  const handleCancel = () => {
    shareStore.reset();
    participantSearchStore.reset();
    const returnPath = searchParams.get('returnPath') || '/learning_collections';
    navigate(returnPath);
  };

  return (
    <>
      <button
        type="button"
        className={clsx(
          cancelPadding(placement),
          'text-primary hover:opacity-80 transition-opacity',
        )}
        style={{ fontWeight: 'var(--font-weight-medium)' }}
        onClick={handleCancel}
        data-intercom-target={`share-cancel-button-${placement}`}
      >
        <T kind="Cancel" />
      </button>
      <button
        type="button"
        className="px-6 py-2.5 bg-accent text-accent-foreground rounded-md transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
        style={{ fontWeight: 'var(--font-weight-medium)' }}
        disabled={!hasConfirmedParticipants || awaitingCourse(shareStore.state)}
        onClick={onConfirm}
        data-intercom-target={`share-confirm-button-${placement}`}
      >
        <T kind="Confirm" />
      </button>
    </>
  );
}

export default observer(ActionButtons);
